import React from 'react';
import { Box, Button, Dialog, Divider, IconButton, styled, Typography } from '@mui/material';
import { styled as materialStyled } from '@mui/material/styles';
import CircleIcon from '@mui/icons-material/Circle';
import CloseSvg from '../../../assets/img/close-icon-blue.svg';
import { GemColor } from '../../../utils/enums/gem-color.enum';
import { theme } from '../../../theme/theme';

interface ColorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectColor: (_color: GemColor) => void;
  currentColorField: string | null;
}

interface IStyledCircleIconProps {
  code: string;
  name: GemColor;
}

const StyledCircleIcon = styled(CircleIcon)<IStyledCircleIconProps>(({ code, name }) => ({
  color: code,
  width: 15,
  height: 15,
  stroke: name === GemColor.White ? theme.palette.common.white : 'none',
}));

const CustomButton = materialStyled(Button)(() => ({
  borderRadius: '8px',
  padding: '4px 8px',
  textAlign: 'center',
  textTransform: 'none',
  fontFamily: 'Satoshi',
  fontSize: 15,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  height: '28px',
  color: theme.palette.black.main,
  border: `1px solid ${theme.palette.shades.whiteBorder2}`,
  backgroundColor: 'transparent',
  '&:hover': {
    color: theme.palette.black.main,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.shades.primaryBrightDark}`,
  },
}));

const colors = [
  {
    code: '#0005FB',
    name: GemColor.Blue,
  },
  {
    code: '#258014',
    name: GemColor.Green,
  },
  {
    code: '#ED3E41',
    name: GemColor.Red,
  },
  {
    code: '#248080',
    name: GemColor.Teal,
  },
  {
    code: '#EAA6D9',
    name: GemColor.Pink,
  },
  {
    code: '#87EDCE',
    name: GemColor.BiColor,
  },
  {
    code: '#CED0A7',
    name: GemColor.MultiColor,
  },
  {
    code: '#D30DFB',
    name: GemColor.Purple,
  },
  {
    code: '#FFFF3A',
    name: GemColor.Yellow,
  },
  {
    code: '#E883EC',
    name: GemColor.Violet,
  },
  {
    code: '#AE684A',
    name: GemColor.Brown,
  },
  {
    code: '#FAA629',
    name: GemColor.Orange,
  },
  {
    code: '#808080',
    name: GemColor.Grey,
  },
  {
    code: '#FEFEFE',
    name: GemColor.White,
  },
  {
    code: '#000000',
    name: GemColor.Black,
  },
  {
    code: '#E1E1E1',
    name: GemColor.Other,
  },
  {
    code: '#FFFFFF00',
    name: GemColor.Nil,
  },
];

const SelectColorModal: React.FC<ColorModalProps> = ({ isOpen, onClose, onSelectColor, currentColorField }) => {
  if (!isOpen) return null;
  let sanitizedColors = colors;

  if (currentColorField === 'primary') {
    sanitizedColors = colors.slice(0, -1);
  } else if (currentColorField === 'secondary') {
    sanitizedColors = colors;
  }

  return (
    <Dialog open={isOpen} PaperProps={{ sx: { borderRadius: '12px', maxWidth: '655px' } }} fullWidth>
      <Box sx={{ overflow: 'auto' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="textBaseLg500">Select Color</Typography>
          <IconButton aria-label="close-button">
            <Box data-testid="close-button" component="img" src={CloseSvg} onClick={onClose} />
          </IconButton>
        </Box>
        <Divider orientation="horizontal" flexItem />
        <Box
          sx={{
            p: 2,
          }}
        >
          {sanitizedColors.map((color) => (
            <CustomButton
              key={color.code}
              variant="outlined"
              onClick={() => {
                onSelectColor(color.name);
                onClose();
              }}
              startIcon={<StyledCircleIcon code={color.code} name={color.name} />}
              sx={{ flex: '1 0 calc(50% - 8px)', margin: 0.5 }} // Makes buttons responsive
            >
              <Typography variant="textBase500">{color.name}</Typography>
            </CustomButton>
          ))}
        </Box>
      </Box>
    </Dialog>
  );
};

export default SelectColorModal;
